import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, X } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO, { BreadcrumbSchema } from "../components/SEO";

const DIR = "/images/projects/";

const PHOTOS = [
  { name: "bcpeabody-north-tampa-custom-home-primary-bath-07.jpg", alt: "Primary bathroom with freestanding tub, backlit onyx column and marble walls, North Tampa custom home" },
  { name: "bcpeabody-north-tampa-custom-home-kitchen-02.jpg", alt: "Custom kitchen with white cabinetry, island and stainless range hood, North Tampa" },
  { name: "bcpeabody-north-tampa-custom-home-pool-bar-03.jpg", alt: "Screened pool with in-water bar seating, North Tampa custom home" },
  { name: "bcpeabody-north-tampa-custom-home-outdoor-kitchen-06.jpg", alt: "Covered outdoor kitchen with built-in grill and mosaic backsplash, North Tampa custom home" },
  { name: "bcpeabody-north-tampa-custom-home-foyer-05.jpg", alt: "Foyer with arched wood double doors and wood-plank tray ceiling, North Tampa custom home", pos: "object-[center_25%]" },
  { name: "bcpeabody-north-tampa-custom-home-groin-vault-dining-04.jpg", alt: "Dining room with groin-vault ceiling, North Tampa custom home", pos: "object-[center_25%]" },
];

const FEATURES = [
  "Screened pool and spa with an in-water bar",
  "Covered outdoor kitchen",
  "Groin-vault and wood-plank tray ceilings",
  "Butler's pantry off the kitchen",
  "Whole-house generator",
];

function Lightbox({ index, setIndex }) {
  const nav = (dir) => setIndex((cur) => (cur + dir + PHOTOS.length) % PHOTOS.length);
  const p = PHOTOS[index];
  return (
    <div data-testid="nt-lightbox" role="dialog" className="fixed inset-0 z-[60] bg-ink/95 flex items-center justify-center p-4" onClick={() => setIndex(null)}>
      <button onClick={() => setIndex(null)} data-testid="nt-lightbox-close" className="absolute top-6 right-6 text-cream-light" aria-label="Close"><X size={28} /></button>
      <button onClick={(e) => { e.stopPropagation(); nav(-1); }} data-testid="nt-lightbox-prev" className="absolute left-4 md:left-10 text-cream-light p-2" aria-label="Previous"><ChevronLeft size={32} /></button>
      <img src={DIR + p.name} alt={p.alt} className="max-w-full max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
      <button onClick={(e) => { e.stopPropagation(); nav(1); }} data-testid="nt-lightbox-next" className="absolute right-4 md:right-10 text-cream-light p-2" aria-label="Next"><ChevronRight size={32} /></button>
    </div>
  );
}

export default function NorthTampaCustomHome() {
  const [lb, setLb] = useState(null);
  return (
    <>
      <SEO
        title="North Tampa Custom Home | BCPeabody"
        description="A single-story custom home in North Tampa built by BCPeabody, with a screened pool, outdoor kitchen and groin-vault dining room."
        path="/projects/north-tampa-custom-home"
        image={`https://bcpeabody.com${DIR}${PHOTOS[0].name}`}
        type="article"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "https://bcpeabody.com/" },
        { name: "Our Work", url: "https://bcpeabody.com/projects" },
        { name: "North Tampa Custom Home", url: "https://bcpeabody.com/projects/north-tampa-custom-home" },
      ]} />
      <Nav />

      <section data-testid="project-hero-north-tampa-custom-home" className="bg-cream-light pt-16 md:pt-20 pb-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-5">
            <Link to="/projects" className="text-[11px] tracking-[0.18em] uppercase text-copper flex items-center gap-2 mb-6 hover:text-copper-deep w-fit">
              <ArrowLeft size={14} /> Back to Our Work
            </Link>
            <div className="text-[11px] tracking-[0.18em] uppercase text-copper mb-4">Residential</div>
            <h1 className="font-serif font-medium text-ink tracking-tight mb-6 leading-[1.05]" style={{ fontSize: "clamp(32px, 5vw, 56px)" }}>North Tampa Custom Home</h1>
            <p className="text-ink/80 leading-relaxed" style={{ fontSize: 17 }}>
              A single-story custom home built by BCPeabody for the Carpenter family. Concrete block and stucco under a tile roof, with marble floors, wood-plank tray ceilings in the foyer and living areas, and a groin-vault dining room.
            </p>
          </div>
          <div className="md:col-span-7 aspect-[4/3] overflow-hidden bg-ink/5">
            <img src={DIR + PHOTOS[0].name} alt={PHOTOS[0].alt} className="w-full h-full object-cover object-[center_60%]" />
          </div>
        </div>
      </section>

      <section data-testid="nt-features" className="bg-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase text-copper mb-8">Key Features</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {FEATURES.map((f, i) => (
              <div key={f} className="flex items-start gap-3 pb-4 border-b border-ink/10">
                <span className="font-serif text-copper text-xl leading-none mt-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-ink/85">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="nt-gallery" className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {PHOTOS.map((p, i) => (
              <button key={p.name} data-testid={`nt-gallery-${i + 1}`} onClick={() => setLb(i)} className="relative aspect-square overflow-hidden bg-ink/5 border border-ink/10 group">
                <img src={DIR + p.name} alt={p.alt} loading="lazy" className={`w-full h-full object-cover ${p.pos || ""} group-hover:scale-105 transition-transform duration-[1200ms]`} />
              </button>
            ))}
          </div>
          <Link to="/custom-homes" data-testid="nt-more-custom-homes" className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-medium text-copper hover:text-copper-deep">
            <ArrowLeft size={14} /> More custom homes
          </Link>
        </div>
      </section>

      <section className="bg-ink text-cream-light py-20 text-center">
        <div className="max-w-[800px] mx-auto px-6">
          <h2 className="font-serif font-medium mb-8 tracking-tight" style={{ fontSize: "clamp(26px, 3vw, 36px)" }}>Planning a custom home?</h2>
          <Link to="/contact" data-testid="nt-cta-start" className="btn-copper">Start a Project</Link>
        </div>
      </section>

      {lb !== null && <Lightbox index={lb} setIndex={setLb} />}
      <Footer />
    </>
  );
}
